import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { ShieldAlert } from "lucide-react";
import React, { useMemo } from "react";

import { CaretIcon } from "../../../../components";
import { type IntegrityTimelineEvent } from "../../AiRecording/IntegritySignalCard/utils";

type IntegrityBarV2Props = {
  callDuration: number;
  integrityEvents: IntegrityTimelineEvent[];
  onClickEvent: (timestamp: number) => void;
};

const EVENT_COLOR = "red.500";
const EVENT_COLOR_HOVER = "red.600";

const IntegrityBarV2: React.FC<IntegrityBarV2Props> = ({
  callDuration,
  integrityEvents,
  onClickEvent,
}) => {
  const { rangeEvents, pointEvents } = useMemo(() => {
    const range = integrityEvents.filter(
      (e) => e.durationSeconds && e.durationSeconds > 0
    );
    const point = integrityEvents.filter(
      (e) => !e.durationSeconds || e.durationSeconds <= 0
    );
    return { rangeEvents: range, pointEvents: point };
  }, [integrityEvents]);

  if (integrityEvents.length === 0) return null;
  if (callDuration <= 0 || !Number.isFinite(callDuration)) return null;

  return (
    <Accordion allowToggle defaultIndex={0}>
      <AccordionItem border="0">
        {({ isExpanded }) => (
          <>
            <Flex align="center" justify="space-between" py="1.5">
              <Flex align="center" gap="1">
                <Icon
                  as={ShieldAlert}
                  boxSize="4"
                  color="red.300"
                  strokeWidth="1.3px"
                />
                <Box fontSize="xs" lineHeight="4" color="whiteAlpha.800">
                  Integrity Signals
                </Box>
              </Flex>
              <AccordionButton
                p={0}
                width="6"
                h="6"
                justifyContent="center"
                alignItems="center"
              >
                <Tooltip
                  label={
                    isExpanded
                      ? "Hide integrity signals"
                      : "Show integrity signals"
                  }
                  shouldWrapChildren
                >
                  <Icon
                    as={CaretIcon}
                    color="gray.100"
                    boxSize="4"
                    transition="all 0.5s ease"
                    style={{
                      rotate: isExpanded ? "180deg" : "0deg",
                      marginTop: "-2px",
                    }}
                  />
                </Tooltip>
              </AccordionButton>
            </Flex>
            <AccordionPanel p={0} pb="1">
              <Box py="2">
                <Flex
                  position="relative"
                  alignItems="center"
                  bg="whiteAlpha.200"
                  borderRadius="2px"
                  h="12px"
                  w="100%"
                  overflow="visible"
                >
                  {rangeEvents.map((event) => {
                    const left = Math.min(0.99, event.timestamp / callDuration);
                    const width = Math.min(
                      0.99 - left,
                      (event.durationSeconds ?? 0) / callDuration
                    );
                    return (
                      <Tooltip
                        key={event.eventKey}
                        label={event.label}
                        placement="top"
                      >
                        <Box
                          role="group"
                          position="absolute"
                          left={`${left * 100}%`}
                          width={`${Math.max(width, 0.003) * 100}%`}
                          minWidth="4px"
                          cursor="pointer"
                          display="flex"
                          alignItems="center"
                          onClick={() => onClickEvent(event.timestamp)}
                        >
                          <Icon
                            as={ShieldAlert}
                            boxSize="4"
                            color={EVENT_COLOR}
                            fill={EVENT_COLOR}
                            stroke="white"
                            strokeWidth="1.5"
                            flexShrink={0}
                            zIndex={1}
                          />
                          <Box
                            h="4px"
                            flex="1"
                            bg="red.300"
                            borderRadius="3px"
                            opacity={0.85}
                            _groupHover={{
                              bg: EVENT_COLOR,
                              opacity: 1,
                            }}
                            transition="opacity 0.2s ease"
                          />
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {pointEvents.map((event) => {
                    const left = Math.min(0.99, event.timestamp / callDuration);
                    return (
                      <Tooltip
                        key={event.eventKey}
                        label={event.label}
                        placement="top"
                      >
                        <Flex
                          position="absolute"
                          left={`${left * 100}%`}
                          transform="translateX(-8px)"
                          cursor="pointer"
                          alignItems="center"
                          _hover={{ "& > svg": { color: EVENT_COLOR_HOVER } }}
                          transition="all 0.15s ease"
                          onClick={() => onClickEvent(event.timestamp)}
                        >
                          <Icon
                            as={ShieldAlert}
                            boxSize="4"
                            color={EVENT_COLOR}
                            fill={EVENT_COLOR}
                            stroke="white"
                            strokeWidth="1.5"
                          />
                        </Flex>
                      </Tooltip>
                    );
                  })}
                </Flex>
              </Box>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default IntegrityBarV2;
